import React from 'react';
import { css } from '@emotion/css';

const policyPage = css({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: 'var(--spacing-lg)',
  minHeight: 'calc(100vh - 100px)',
});

const policyPageContainer = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
});

const policyPageHeader = css({
  marginBottom: 'var(--spacing-lg)',
  borderBottom: '2px solid var(--pc-200)',
  paddingBottom: 'var(--spacing-md)',
});

const policyPageTitle = css({
  fontSize: 'var(--fs-800)',
  fontWeight: 700,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-xs)',
});

const policyPageContent = css({
  fontSize: 'var(--fs-400)',
  lineHeight: 1.6,
  color: 'var(--g-800)',
});

const policyPageSection = css({
  marginBottom: 'var(--spacing-lg)',
});

const policyPageSectionTitle = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 600,
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-sm)',
  paddingBottom: 'var(--spacing-xs)',
  borderBottom: '1px solid var(--pc-100)',
});

const policyPageParagraph = css({
  marginBottom: 'var(--spacing-md)',
});

const policyPageList = css({
  listStyleType: 'disc',
  paddingLeft: 'var(--spacing-lg)',
  marginBottom: 'var(--spacing-md)',
});

const policyPageListItem = css({
  marginBottom: 'var(--spacing-xs)',
});

const policyPageDate = css({
  fontStyle: 'italic',
  marginTop: 'var(--spacing-lg)',
  color: 'var(--g-600)',
  fontSize: 'var(--fs-350)',
});

// Media queries for responsiveness
const responsiveStyles = css({
  '@media (max-width: 768px)': {
    [`&.${policyPage}`]: {
      padding: 'var(--spacing-md)',
    },
    [`.${policyPageTitle}`]: {
      fontSize: 'var(--fs-700)',
    },
    [`.${policyPageSectionTitle}`]: {
      fontSize: 'var(--fs-500)',
    },
  },
  '@media (max-width: 480px)': {
    [`&.${policyPage}`]: {
      padding: 'var(--spacing-sm)',
    },
    [`.${policyPageTitle}`]: {
      fontSize: 'var(--fs-600)',
    },
    [`.${policyPageContent}`]: {
      fontSize: 'var(--fs-350)',
    },
    [`.${policyPageSectionTitle}`]: {
      fontSize: 'var(--fs-450)',
    },
  },
});

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className={`${policyPage} ${responsiveStyles}`}>
      <div className={policyPageContainer}>
        <div className={policyPageHeader}>
          <h1 className={policyPageTitle}>Políticas de Privacidade</h1>
        </div>

        <div className={policyPageContent}>
          <p className={policyPageParagraph}>
            A sua privacidade é importante para nós. É política da Anchor Ingressos respeitar a sua 
            privacidade em relação a qualquer informação sua que possamos coletar no site Anchor Ingressos, 
            e outros sites que possuímos e operamos (Anchor Experiences e outros).
          </p>

          <p className={policyPageParagraph}>
            Solicitamos informações pessoais apenas quando realmente precisamos delas para lhe fornecer um 
            serviço. Fazemo-lo por meios justos e legais, com o seu conhecimento e consentimento. Também 
            informamos por que estamos coletando e como será usado.
          </p>

          <p className={policyPageParagraph}>
            Apenas retemos as informações coletadas pelo tempo necessário para fornecer o serviço solicitado. 
            Quando armazenamos dados, protegemos dentro de meios comercialmente aceitáveis ​​para evitar perdas 
            e roubos, bem como acesso, divulgação, cópia, uso ou modificação não autorizados.
          </p>

          <p className={policyPageParagraph}>
            Não compartilhamos informações de identificação pessoal publicamente ou com terceiros, exceto quando 
            exigido por lei.
          </p>

          <p className={policyPageParagraph}>
            O nosso site pode ter links para sites externos que não são operados por nós (tiqueteiras – 
            plataformas de venda de ingressos, mapas, redes sociais, etc.). Esteja ciente de que não temos 
            controle sobre o conteúdo e práticas desses sites e não podemos aceitar responsabilidade por suas 
            respectivas políticas de privacidade.
          </p>

          <p className={policyPageParagraph}>
            Você é livre para recusar a nossa solicitação de informações pessoais, entendendo que talvez não 
            possamos fornecer alguns dos serviços desejados.
          </p>

          <p className={policyPageParagraph}>
            O uso continuado de nosso site será considerado como aceitação de nossas práticas em torno de 
            privacidade e informações pessoais. Se você tiver alguma dúvida sobre como lidamos com dados do 
            usuário e informações pessoais, entre em contacto conosco.
          </p>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Cookies e Publicidade</h3>
            <p className={policyPageParagraph}>
              O serviço Google AdSense que usamos para veicular publicidade usa um cookie DoubleClick para 
              veicular anúncios mais relevantes em toda a Web e limitar o número de vezes que um determinado 
              anúncio é exibido para você.
            </p>
            <p className={policyPageParagraph}>
              Para mais informações sobre o Google AdSense, consulte as FAQs oficiais sobre privacidade do 
              Google AdSense.
            </p>
            <p className={policyPageParagraph}>
              Utilizamos anúncios para compensar os custos de funcionamento deste site e fornecer financiamento 
              para futuros desenvolvimentos. Os cookies de publicidade comportamental usados por este site foram 
              projetados para garantir que você forneça os anúncios mais relevantes sempre que possível, 
              rastreando anonimamente seus interesses e apresentando coisas semelhantes que possam ser do seu 
              interesse.
            </p>
            <p className={policyPageParagraph}>
              Vários parceiros anunciam em nosso nome e os cookies de rastreamento de afiliados simplesmente 
              nos permitem ver se nossos clientes acessaram o site através de um dos sites de nossos parceiros, 
              para que possamos creditá-los adequadamente e, quando aplicável, permitir que nossos parceiros 
              afiliados ofereçam qualquer promoção que pode fornecê-lo para fazer uma compra.
            </p>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Compromisso do Usuário</h3>
            <p className={policyPageParagraph}>
              O usuário se compromete a fazer uso adequado dos conteúdos e da informação que a Anchor Ingressos 
              oferece no site e com caráter enunciativo, mas não limitativo:
            </p>
            <ul className={policyPageList}>
              <li className={policyPageListItem}>
                Não se envolver em atividades que sejam ilegais ou contrárias à boa fé a à ordem pública;
              </li>
              <li className={policyPageListItem}>
                Não difundir propaganda ou conteúdo de natureza racista, xenofóbica, betano apostas ou azar, 
                qualquer tipo de pornografia ilegal, de apologia ao terrorismo ou contra os direitos humanos;
              </li>
              <li className={policyPageListItem}>
                Não causar danos aos sistemas físicos (hardwares) e lógicos (softwares) do Anchor Experiences, 
                de seus fornecedores ou terceiros, para introduzir ou disseminar vírus informáticos ou quaisquer 
                outros sistemas de hardware ou software que sejam capazes de causar danos anteriormente mencionados.
              </li>
            </ul>
          </div>

          <div className={policyPageSection}>
            <h3 className={policyPageSectionTitle}>Mais informações</h3>
            <p className={policyPageParagraph}>
              Esperemos que esteja esclarecido e, como mencionado anteriormente, se houver algo que você não tem 
              certeza se precisa ou não, geralmente é mais seguro deixar os cookies ativados, caso interaja com 
              um dos recursos que você usa em nosso site.
            </p>
          </div>

          <p className={policyPageDate}>
            Esta política é válida e vigente a partir de 11 de abril de 2026, 07:37.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
